import { PencilEditIcon } from "@images/svgIcons/pencilEditIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { UserCellSkeleton } from "@modules/members/components/userCellSkeleton"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { Hourglass, Mail, Phone, Trash2 } from "lucide-react"
import { Skeleton } from "primereact/skeleton"
import { useTheme } from "styled-components"

type BoxLeadOverviewSkeletonProps = {
  className?: string
} & React.ComponentProps<"div">

export function BoxLeadOverviewSkeleton({ className, ...props }: BoxLeadOverviewSkeletonProps) {
  const defaultClassName = "flex flex-column w-full gap-5"

  const theme = useTheme()

  return (
    <div className={className ?? defaultClassName} {...props}>
      {/* HEADER */}
      <div className="grid">
        <div className="col-4">
          <div className="flex flex-column gap-1">
            <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
              {word("box.lead.overview.content.firstContact.label").toUpperCase()}
            </HUText>
            <Skeleton height="1rem" className="border-round-sm" width="60%" />
          </div>
        </div>
        <div className="col-4">
          <div className="flex flex-row gap-1 align-items-center">
            <Hourglass size={24} color={theme.mainWhite} strokeWidth={1.5} />
            <Skeleton height="1rem" className="border-round-sm" width="60%" />
          </div>
        </div>
        <div className="col-4">
          <div className="flex flex-column gap-1">
            <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
              {word("box.lead.overview.content.madeBy.label").toUpperCase()}
            </HUText>
            <UserCellSkeleton subtitle={true} />
          </div>
        </div>
      </div>

      {/* CONTACT */}
      <div className="flex flex-column gap-2">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("global.form.label.contact")}
        </HUText>
        <div className="grid">
          <div className="col-7 flex flex-row gap-2 align-items-center">
            <Mail strokeWidth={1.5} color={theme.neutralColor600} size={22} />
            <Skeleton height="1.125rem" className="border-round-sm" width="100%" />
          </div>
          <div className="col-5 flex flex-row gap-2 align-items-center">
            <Phone strokeWidth={1.5} color={theme.neutralColor600} size={22} />
            <Skeleton height="1.125rem" className="border-round-sm" width="100%" />
          </div>
        </div>
      </div>

      {/* GOALS AND EXPERIENCE LEVEL */}
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-column gap-2 w-5">
          <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
            {word("box.lead.overview.content.goals.label", { count: 1 }).toUpperCase()}
          </HUText>
          <Skeleton height="1rem" className="border-round-sm" width="100%" />
        </div>
        <div className="flex flex-column gap-2 w-3">
          <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
            {word("box.lead.overview.content.experienceLevel.label").toUpperCase()}
          </HUText>
          <Skeleton height="1rem" className="border-round-sm" width="100%" />
        </div>
      </div>

      {/* HEARD ABOUT US */}
      <div className="flex flex-column gap-2">
        <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
          {word("box.lead.overview.content.hearAboutUs.label").toUpperCase()}
        </HUText>
        <Skeleton height="1rem" className="border-round-sm" width="40%" />
      </div>

      {/* BEST TIME TO CALL */}
      <div className="flex flex-column gap-2">
        <HUText type="label" fontStyle="LS" color={theme.neutralColor600}>
          {word("box.lead.overview.content.bestTimeToContact.label").toUpperCase()}
        </HUText>
        <Skeleton height="1rem" className="border-round-sm" width="60%" />
      </div>

      {/* INTERNAL NOTE*/}
      <div className="flex flex-column gap-2">
        <HUText fontStyle="TM" color={theme.mainWhite}>
          {word("global.form.label.internalNote")}
        </HUText>
        <div className="flex flex-row gap-2 mt-2 mb-2">
          <WarningIcon color={theme.neutralColor900} width={24} height={24} className="w-1" />
          <HUText fontStyle="BS" color={theme.neutralColor400}>
            {word("box.lead.form.input.internalNote.warning")}
          </HUText>
        </div>
        <Skeleton height="7rem" borderRadius="8px" />
      </div>

      {/* BUTTONS */}
      <div className=" mb-4 flex flex-row gap-2 align-items-center justify-content-center">
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Quaternary"
          text={word("box.lead.overview.content.button.deleteLead")}
          className="w-full"
          disabled={true}
          icon={{
            iconView: <Trash2 color={theme.mainWhite} size={15} />,
          }}
        />
        <HUButton
          type="Left_Icon"
          size="M"
          colorType="Primary"
          text={word("global.edit")}
          submit
          className="w-full"
          disabled={true}
          icon={{
            iconView: <PencilEditIcon color={theme.mainWhite} size={15} />,
          }}
        />
      </div>
    </div>
  )
}
